import { useState } from "react";
import { motion } from "framer-motion";
import { Menu, X, BadgePercent } from "lucide-react";
import { Logo } from "./Logo";
import { useLanguage } from "../i18n/translations";

const LANGS = [
  { id: "en", label: "EN" },
  { id: "hi", label: "हिंदी" },
  { id: "hinglish", label: "Hinglish" },
];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#services", label: t.nav.services },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#surya-ghar", label: t.nav.scheme },
    { href: "#equipment", label: t.nav.equipment },
    { href: "#contact", label: t.nav.contact },
  ];

  return (
    <header className="sticky top-0 z-50 border-b border-white/10 bg-navy-950/80 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
        <a href="#top" className="flex items-center" data-testid="header-logo">
          <Logo size={46} />
        </a>

        <nav className="hidden items-center gap-7 lg:flex">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.href.slice(1)}`}
              className="text-sm font-medium text-slate-300 transition-colors duration-200 hover:text-solar-yellow"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <div className="flex rounded-full border border-white/10 bg-navy-900 p-1" data-testid="header-lang-toggle">
            {LANGS.map((l) => (
              <button
                key={l.id}
                onClick={() => setLang(l.id)}
                data-testid={`lang-${l.id}-btn`}
                className={`rounded-full px-2.5 py-1 text-xs font-semibold transition-colors duration-200 ${
                  lang === l.id ? "bg-solar text-amber-950" : "text-slate-400 hover:text-white"
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <span className="hidden items-center gap-1.5 rounded-full border border-solar/40 bg-solar/10 px-3 py-1.5 text-xs font-bold text-solar-highlight xl:flex">
            <BadgePercent size={14} /> 0% Finance*
          </span>

          <a
            href="#contact"
            data-testid="header-quote-btn"
            className="hidden rounded-full bg-solar px-5 py-2 text-sm font-bold text-amber-950 transition-colors duration-200 hover:bg-solar-yellow sm:block"
          >
            {t.nav.quote}
          </a>

          <button
            className="text-white lg:hidden"
            onClick={() => setOpen(!open)}
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t border-white/10 bg-navy-950 px-6 py-4 lg:hidden"
        >
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="block py-3 text-sm font-medium text-slate-200 hover:text-solar-yellow"
              data-testid={`mobile-nav-link-${l.href.slice(1)}`}
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            className="mt-2 block rounded-full bg-solar px-5 py-2.5 text-center text-sm font-bold text-amber-950"
            data-testid="mobile-quote-btn"
          >
            {t.nav.quote}
          </a>
        </motion.nav>
      )}
    </header>
  );
};
